import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    Popover,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import {
    getGetDeviceGroupsKey,
    useDeleteDeviceGroup,
    useGetDeviceGroupByID,
    useGetDeviceSpecByID,
    useGetUserDeviceGroupsLastSeen,
    useUpdateDeviceGroup,
} from 'gen/client';
import { UserUIDeviceGroupResponseDTO } from 'gen/model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mutate } from 'swr';

import { unsuppressInstallation } from '@ui/Components/DeviceDiscovery/discoverySuppression';
import { useRenkiConfig } from '@ui/Utils/device/installationMetadata';

import { dayjs } from 'kerberus-components/src/Components/Dayjs/dayjs-utils';
import ConfirmationDialog from 'kerberus-components/src/Components/Modals/ConfirmationDialog';
import DeviceInformationModal from 'kerberus-components/src/Components/Modals/DeviceInformationModal';
import { autoScan } from 'kerberus-components/src/Utils/device/autoScan';
import { customPromiseToast, deviceDeletePromise } from 'kerberus-components/src/Utils/toasts';

import ViewContainer from '../../../Components/Layout/ViewContainer';
import DataGridMacAddress from './DatagridMacAddress';
import DeviceContent from './DeviceContent';

export default function DeviceView() {
    const { groupId } = useParams();

    const { data: deviceData, mutate: mutateDeviceData } = useGetDeviceGroupByID(groupId!);
    const { data: deviceSpec } = useGetDeviceSpecByID(deviceData?.devicespec_id ?? 0, {
        swr: {
            enabled: !!deviceData?.devicespec_id,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    });

    const { trigger } = useUpdateDeviceGroup(groupId!, {
        swr: {
            onSuccess: (data) => {
                void mutateDeviceData((prev) => ({ ...(prev || ({} as UserUIDeviceGroupResponseDTO)), ...data }), {
                    revalidate: false,
                });
                void mutate(getGetDeviceGroupsKey());
            },
        },
    });

    const { trigger: triggerDeletion } = useDeleteDeviceGroup(groupId!, {
        swr: {
            onSuccess: () => {
                void mutate(getGetDeviceGroupsKey());
            },
        },
    });

    const { data: onlineDevs } = useGetUserDeviceGroupsLastSeen(
        {},
        {
            swr: {
                refreshInterval: 60_000,
            },
        },
    );

    const { config, updateConfig } = useRenkiConfig();

    const tauri = isTauri();

    const theme = useTheme();
    const navigate = useNavigate();
    const [nameEdit, setNameEdit] = useState<(EventTarget & HTMLButtonElement) | null>(null);
    const [devName, setDevName] = useState(deviceData?.group_name);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<UserUIDeviceGroupResponseDTO>();
    const [informationModal, setInformationModal] = useState(false);

    const handleEditName = (event: React.MouseEvent<HTMLButtonElement>) => {
        setNameEdit(event.currentTarget);
    };
    const closeEditName = () => {
        setNameEdit(null);
    };

    const handleOpenInformationModal = () => {
        setInformationModal(true);
    };
    const handleCloseInformationModal = () => {
        setInformationModal(false);
    };

    const [lastSeen, lastSeenMinutes] = useMemo(() => {
        const today = dayjs();

        if (!onlineDevs) {
            return [null, null];
        }
        const activeDevice = onlineDevs.filter((dev) => deviceData?.id === dev.group_id);

        if (activeDevice.length === 0) {
            return [null, null];
        }

        const selectActiveDevice = activeDevice[0]!;

        const lastsmns = dayjs.duration(today.diff(selectActiveDevice.timestamp)).asMinutes();
        return [selectActiveDevice, lastsmns];
    }, [onlineDevs, deviceData]);

    const contentDelete = (
        <Typography component="div" sx={{ overflowWrap: 'break-word' }}>
            Are you sure you want to delete
            <span style={{ fontWeight: 'bold' }}>{selectedGroup ? ` ${selectedGroup.group_name}? ` : ''}</span>
            This will remove device from the list and stop tracking.
        </Typography>
    );

    // Change device group_name
    const handleDeviceName = useCallback(async () => {
        if (!groupId) {
            return;
        }
        try {
            await trigger({
                group_name: devName,
            });
        } catch (error) {
            alert(error);
        }
    }, [devName, groupId, trigger]);

    // Delete device group
    const handleDeleteDevGroup = useCallback(
        async (item: UserUIDeviceGroupResponseDTO) => {
            try {
                if (!item.id) {
                    return;
                }
                await customPromiseToast(triggerDeletion(), deviceDeletePromise);
                if (config?.discoverIgnore) {
                    void updateConfig({ discoverIgnore: undefined });
                }
                // If deleting the current installation's device group, remove suppression so future discovery can prompt
                if (config?.installationId && item.installation_id === config.installationId) {
                    unsuppressInstallation(config.installationId);
                }
                navigate('/device');
            } catch (error) {
                alert(error);
            }
        },
        [config, navigate, triggerDeletion, updateConfig],
    );

    const localHandleDelete = useCallback(() => {
        if (!selectedGroup) {
            return;
        }
        void handleDeleteDevGroup(selectedGroup);
    }, [selectedGroup, handleDeleteDevGroup]);

    useEffect(() => {
        if (deviceData?.group_name) {
            setDevName(deviceData.group_name);
        }
    }, [deviceData?.group_name]);

    return deviceData ? (
        <Container disableGutters maxWidth={false}>
            <ViewContainer px={{ lg: 3, xs: 0 }} py={{ lg: 2, xs: 0 }}>
                <Stack spacing={{ lg: 2, xs: 0 }}>
                    <Stack spacing={0}>
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'flex-start',
                                display: { lg: 'flex', xs: 'none' },
                                justifyContent: 'space-between',
                                px: { md: 0, xs: 0.5 },
                                width: '100%',
                            }}
                        >
                            <Stack spacing={0.5} sx={{ mb: 1.5, px: { md: 0, xs: 0.5 } }}>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                    <IconButton
                                        onClick={() => {
                                            navigate('/device');
                                        }}
                                        size="small"
                                    >
                                        <ArrowBackIcon color="primary" />
                                    </IconButton>
                                    <Typography
                                        sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: 0.2 }}
                                        variant="h5"
                                    >
                                        {deviceData.group_name}
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ alignItems: 'center', flexWrap: 'wrap', height: 24 }}
                                >
                                    <Typography sx={{ color: 'text.secondary', fontWeight: 500 }} variant="body2">
                                        Manage this device and its settings
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack direction="row">
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                    {tauri && deviceData.installation_id ? (
                                        <Button
                                            onClick={() =>
                                                autoScan(tauri, deviceData.installation_id!).catch((error) =>
                                                    console.error(error),
                                                )
                                            }
                                            variant="contained"
                                        >
                                            Update
                                        </Button>
                                    ) : null}
                                    <IconButton onClick={handleEditName}>
                                        <EditIcon color="primary" />
                                    </IconButton>
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => {
                                            setSelectedGroup(deviceData);
                                            setOpenDelete(true);
                                        }}
                                    >
                                        <DeleteIcon sx={{ color: theme.palette.mlRed.main }} />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            <Popover
                                anchorEl={nameEdit}
                                anchorOrigin={{
                                    horizontal: 'center',
                                    vertical: 'bottom',
                                }}
                                onClose={closeEditName}
                                open={!!nameEdit}
                                transformOrigin={{
                                    horizontal: 'center',
                                    vertical: 'top',
                                }}
                            >
                                <Box
                                    sx={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        p: 1,
                                    }}
                                >
                                    <TextField
                                        label="New name"
                                        onChange={(e) => {
                                            setDevName(e.target.value);
                                        }}
                                        value={devName}
                                    />
                                    <Button
                                        onClick={() => {
                                            void handleDeviceName();
                                            closeEditName();
                                        }}
                                        sx={{ ml: 1 }}
                                        variant="contained"
                                    >
                                        Ok
                                    </Button>
                                </Box>
                            </Popover>

                            <ConfirmationDialog
                                content={contentDelete}
                                handleDevice={localHandleDelete}
                                name={selectedGroup?.group_name ?? 'Device'}
                                open={openDelete}
                                setOpen={setOpenDelete}
                                title={['Delete']}
                            />
                        </Stack>

                        {/* Mobile header - visible only on mobile */}
                        <Stack
                            direction="column"
                            spacing={1.5}
                            sx={{
                                backgroundColor: '#edf2f6',
                                display: { lg: 'none', xs: 'flex' },
                                px: 2,
                                py: 1,
                                width: '100%',
                            }}
                        >
                            {/* Device name with edit button & delete */}
                            <Stack
                                direction="row"
                                sx={{
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <Box sx={{ flex: 1, mr: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '1.1rem',
                                            fontWeight: 'medium',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {deviceData.group_name}
                                    </Typography>
                                </Box>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        alignItems: 'flex-start',
                                        flexShrink: 0,
                                    }}
                                >
                                    <IconButton onClick={handleEditName} size="small">
                                        <EditIcon color="primary" fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => {
                                            setSelectedGroup(deviceData);
                                            setOpenDelete(true);
                                        }}
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" sx={{ color: theme.palette.mlRed.main }} />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            {/* Update button if in Tauri */}
                            {tauri && deviceData.installation_id ? (
                                <Button
                                    onClick={() =>
                                        autoScan(tauri, deviceData.installation_id!).catch((error) =>
                                            console.error(error),
                                        )
                                    }
                                    size="small"
                                    sx={{ alignSelf: 'flex-end', mr: 1 }}
                                    variant="contained"
                                >
                                    Update Device
                                </Button>
                            ) : null}
                        </Stack>
                    </Stack>

                    <Stack>
                        <DeviceContent
                            deviceGroup={deviceData}
                            deviceSpec={deviceSpec}
                            lastSeen={lastSeen}
                            lastSeenMinutes={lastSeenMinutes}
                            openInfo={handleOpenInformationModal}
                        />
                        <DataGridMacAddress deviceData={deviceData} />
                    </Stack>
                    <DeviceInformationModal
                        deviceGroup={deviceData}
                        deviceSpec={deviceSpec}
                        handleClose={handleCloseInformationModal}
                        open={informationModal}
                    />
                </Stack>
            </ViewContainer>
        </Container>
    ) : (
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, flexDirection: 'column', justifyContent: 'center' }}>
            <CircularProgress size={60} thickness={5} />
            <Typography
                color="primary"
                sx={{
                    fontWeight: 'bold',
                    mt: 4,
                }}
                variant="h6"
            >
                Loading device data...
            </Typography>
        </Box>
    );
}
